import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAllEngagements } from '../../store/actions';
import { GenCustomTable } from '../../components/customComponents/genCustomTable/GenCustomTable';
import { ChevronRight, AlertTriangle } from 'lucide-react';
import Header from '../../components/layout/Header';
import Badge, { statusToBadge } from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { ENGAGEMENT_STAGE, ENGAGEMENT_STATUS, formatDate, formatINR, label, options } from '../../constants/garmentCodes';

export default function BrandEngagements() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const manufacturerId = params.get('manufacturerId') || '';
  const engagements = useSelector((state) => state.engagement.engagements);
  const loading = useSelector((state) => state.engagement.loading);
  const brandId = useSelector((state) => state.brand.profile?.id);

  useEffect(() => {
    dispatch(getAllEngagements({ brandId, manufacturerId, status: '' }));
  }, [dispatch, brandId, manufacturerId]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header title="Engagements" subtitle={`${engagements.length} engagements`} />
      <div className="flex-1 overflow-y-auto p-6">
        <div className="flex items-center gap-3 mb-4">
          {manufacturerId && <button onClick={() => navigate('/brand/engagements')} className="text-xs text-[#3D52A0] hover:underline">Clear manufacturer filter</button>}
          {loading && <span className="text-xs text-[#9B9893]">Loading…</span>}
        </div>

        <div className="bg-white rounded-xl border border-[#E5E3DE] overflow-hidden [&_.MuiTablePagination-toolbar]:!justify-end [&_.MuiTablePagination-spacer]:!flex-1 [&_.MuiTablePagination-spacer]:!block [&_.MuiTablePagination-selectLabel]:!m-0 [&_.MuiTablePagination-displayedRows]:!m-0 [&_.MuiTablePagination-root]:!text-[#5C5A54]">
          <GenCustomTable
            isExportEnabled={true}
            exportFileName="Engagements"
            columns={[
              { id: 'engagement', label: 'ENGAGEMENT', align: 'left' },
              { id: 'manufacturer', label: 'MANUFACTURER', align: 'left' },
              { id: 'status', label: 'STATUS', align: 'left' },
              { id: 'stage', label: 'STAGE', align: 'left' },
              { id: 'balance', label: 'BALANCE', align: 'left' },
              { id: 'held', label: 'HELD', align: 'left' },
              { id: 'available', label: 'AVAILABLE', align: 'left' },
              { id: 'decisions', label: 'DECISIONS', align: 'left' },
              { id: 'orders', label: 'ORDERS', align: 'left' },
              { id: 'lastActivity', label: 'LAST ACTIVITY', align: 'left' },
              { id: 'action', label: 'ACTIONS', align: 'center', disableFilter: true },
            ]}
            rows={engagements.map((eng) => {
              const stat = label(ENGAGEMENT_STATUS, eng.status);
              const stage = label(ENGAGEMENT_STAGE, eng.stage);
              const decisions = eng.pendingApprovals ?? 0;
              return {
                id: eng.id,
                rColumns: [
                  <div key="eng" label={eng.name || ''} className="flex flex-col justify-center items-start w-full h-full px-2 py-1">
                    <p className="font-semibold text-[#1A1915]">{eng.name}</p>
                    <p className="text-xs text-[#9B9893]">Started {formatDate(eng.startDate)}</p>
                  </div>,
                  <div key="mfg" label={eng.manufacturerName || ''} className="flex items-center w-full h-full px-2 py-1 text-[#5C5A54]">{eng.manufacturerName || '—'}</div>,
                  <div key="status" label={stat} className="flex items-center w-full h-full px-2 py-1"><Badge variant={statusToBadge(stat)}>{stat}</Badge></div>,
                  <div key="stage" label={stage} className="flex items-center w-full h-full px-2 py-1"><Badge variant="gray">{stage}</Badge></div>,
                  <div key="bal" label={formatINR(eng.walletBalance)} className="flex items-center w-full h-full px-2 py-1 font-semibold text-[#1A1915]">{formatINR(eng.walletBalance)}</div>,
                  <div key="held" label={formatINR(eng.heldAmount)} className="flex items-center w-full h-full px-2 py-1 text-[#92560C] font-medium">{formatINR(eng.heldAmount)}</div>,
                  <div key="avail" label={formatINR(eng.availableBalance)} className="flex items-center w-full h-full px-2 py-1 text-[#1A6B3E] font-semibold">{formatINR(eng.availableBalance)}</div>,
                  <div key="dec" label={String(decisions)} className="flex items-center w-full h-full px-2 py-1">
                    {decisions > 0 ? <span className="flex items-center gap-1 text-[#C0222B] font-semibold"><AlertTriangle size={11} />{decisions}</span> : <span className="text-[#9B9893]">0</span>}
                  </div>,
                  <div key="ord" label={String(eng.activeOrders ?? 0)} className="flex items-center w-full h-full px-2 py-1 text-[#5C5A54]">{eng.activeOrders ?? 0}</div>,
                  <div key="act" label={eng.lastActivity || ''} className="flex items-center w-full h-full px-2 py-1 text-xs text-[#9B9893]">{formatDate(eng.lastActivity)}</div>,
                  <div key="action" label="" className="flex items-center justify-center w-full h-full px-2 py-1">
                    <Button variant="ghost" size="xs" iconRight={<ChevronRight size={11} />} onClick={() => navigate(`/brand/wallet?engagementId=${eng.id}`)}>Open</Button>
                  </div>,
                ],
              };
            })}
          />
          {!loading && engagements.length === 0 && <p className="py-10 text-center text-sm text-[#9B9893]">No engagements</p>}
        </div>
      </div>
    </div>
  );
}
